// Fixed sets of callings that grant in-app access, replacing the
// per-stake calling-template config. Ward callings apply to every ward
// scope, branch callings to every branch scope, stake callings to the
// stake scope. Names match `callingSortOrder.ts` verbatim (typo-guarded
// by a unit test).
//
// The three arrays below are the churchwide base list and are the same
// for every stake. One further unit calling — Elders Quorum President —
// is stake-gated rather than hard-coded: it grants access only when the
// stake opts in via `stake.eq_president_app_access`, threaded through as
// `AppAccessOptions.eqPresidentAccess`, and it applies to ward and
// branch scopes alike. The stake set is never affected by the opt-in.
//
// The branch set is not the ward set with names swapped: a branch has no
// counterpart to Ward Executive Secretary, and no Assistant Clerk of
// either unit kind grants access.
export const WARD_APP_ACCESS_CALLINGS = [
    'Bishop',
    'Bishopric First Counselor',
    'Bishopric Second Counselor',
    'Ward Clerk',
    'Ward Executive Secretary',
];
export const BRANCH_APP_ACCESS_CALLINGS = [
    'Branch President',
    'Branch Presidency First Counselor',
    'Branch Presidency Second Counselor',
    'Branch Clerk',
];
export const STAKE_APP_ACCESS_CALLINGS = [
    'Stake President',
    'Stake Presidency First Counselor',
    'Stake Presidency Second Counselor',
    'Stake Clerk',
    'Stake Executive Secretary',
    'Stake High Councilor',
];
/** The one stake-gated unit calling — ward and branch scopes alike.
 * Exact title only — the quorum's counselors and secretary never grant
 * access. */
export const EQ_PRESIDENT_CALLING = 'Elders Quorum President';
/**
 * WRITER-SIDE POLICY (D26). App-access callings whose derived access is
 * the LIMITED tier (D25) rather than full.
 *
 * Consulted **only** where a writer inserts or replaces an access
 * record's calling list — `syncApplyFix` and `backfillEqPresidentAccess`
 * — and never on a read path. The tier is decided once, at write time,
 * and stored in `Access.importer_limited_callings[scope]`; every reader
 * (claim minter, App Access page) reads that stored field. Deriving a
 * tier from a calling name at read time would let the page and the claim
 * minter disagree, and would silently re-tier existing records the moment
 * this constant changed.
 *
 * Consequence of storing it: changing this set does NOT re-tier records
 * already written. They keep whatever the writer stamped until that scope
 * is next written.
 *
 * Keyed on the calling name alone — not on the scope, and not on the
 * unit's kind. A branch Elders Quorum President therefore lands on the
 * limited tier on exactly the same terms as a ward one.
 */
export const LIMITED_TIER_CALLINGS = new Set([EQ_PRESIDENT_CALLING]);
// Normalisation key — same scheme as `callingSortOrder.ts` (trim +
// lowercase). Matching is exact on this key.
function normalize(calling) {
    return calling.trim().toLowerCase();
}
const LIMITED_TIER_SET = new Set([...LIMITED_TIER_CALLINGS].map(normalize));
const WARD_SET = new Set(WARD_APP_ACCESS_CALLINGS.map(normalize));
const BRANCH_SET = new Set(BRANCH_APP_ACCESS_CALLINGS.map(normalize));
const STAKE_SET = new Set(STAKE_APP_ACCESS_CALLINGS.map(normalize));
const WARD_SET_WITH_EQ = new Set([...WARD_APP_ACCESS_CALLINGS, EQ_PRESIDENT_CALLING].map(normalize));
const BRANCH_SET_WITH_EQ = new Set([...BRANCH_APP_ACCESS_CALLINGS, EQ_PRESIDENT_CALLING].map(normalize));
/**
 * Normalised app-access calling set for a scope. `'stake'` → the stake
 * set, whatever `opts.unitType` says. Any other scope (a `ward_code`) →
 * the branch set when `opts.unitType === 'branch'`, the ward set
 * otherwise — **including when `unitType` is absent**, which is both
 * backward-compatible and the fail-closed direction (see
 * {@link AppAccessOptions.unitType}). With `opts.eqPresidentAccess`,
 * whichever unit set is chosen additionally carries Elders Quorum
 * President; the stake set is returned unchanged either way.
 */
export function appAccessCallingsForScope(scope, opts) {
    if (scope === 'stake')
        return STAKE_SET;
    const withEq = opts?.eqPresidentAccess === true;
    if (opts?.unitType === 'branch')
        return withEq ? BRANCH_SET_WITH_EQ : BRANCH_SET;
    return withEq ? WARD_SET_WITH_EQ : WARD_SET;
}
/**
 * Subset of `callings` whose normalised form grants app access for the
 * given scope. Original casing is preserved on the returned values.
 * A non-stake scope needs `opts.unitType` to be read as a branch; see
 * {@link appAccessCallingsForScope}.
 */
export function filterAppAccessCallings(scope, callings, opts) {
    const allowed = appAccessCallingsForScope(scope, opts);
    return callings.filter((c) => allowed.has(normalize(c)));
}
/**
 * WRITER-SIDE. Subset of `callings` whose normalised form is
 * limited-tier ({@link LIMITED_TIER_CALLINGS}). Original casing is
 * preserved, so the result is a literal subset of the input and can be
 * stored verbatim in `Access.importer_limited_callings[scope]` beside the
 * `importer_callings[scope]` it was derived from.
 *
 * Callers pass the already-app-access-filtered list
 * (`filterAppAccessCallings`), so the result names exactly the granted
 * callings that confer limited access. Never call this on a read path —
 * readers consult the stored field.
 */
export function filterLimitedTierCallings(callings) {
    return callings.filter((c) => LIMITED_TIER_SET.has(normalize(c)));
}
//# sourceMappingURL=appAccessCallings.js.map